<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router';

type Variant = 'horizontal' | 'compact' | 'mark';
type Theme = 'light' | 'dark';

const {
  to = '/',
  variant = 'horizontal',
  theme = 'light',
  size,
  link = true,
  geo = 'Dubai',
  ariaLabel = 'Insiders of Dubai',
} = defineProps<{
  to?: RouteLocationRaw;
  variant?: Variant;
  theme?: Theme;
  size?: number;
  link?: boolean;
  geo?: string;
  ariaLabel?: string;
}>();

// NuxtLink doesn't resolve through `:is="'NuxtLink'"` (renders a dead
// <nuxtlink> element with no href). Resolve the component object instead.
const NuxtLinkComponent = resolveComponent('NuxtLink');

const styles = computed(() => {
  if (size == null) return {};
  if (variant === 'mark') {
    return {
      width: `${size}px`,
      height: `${size}px`,
      fontSize: `${Math.round(size * 0.51)}px`,
    };
  }
  return { fontSize: `${size}px` };
});
</script>

<template>
  <component
    :is="link ? NuxtLinkComponent : 'span'"
    v-bind="link ? { to, 'no-prefetch': true } : {}"
    :aria-label="ariaLabel"
    class="dic-logo"
    :class="[`dic-logo--${variant}`, `dic-logo--${theme}`]"
    :style="styles"
  >
    <template v-if="variant === 'mark'">
      <span aria-hidden="true">I</span>
      <span
        class="dic-logo__slash"
        aria-hidden="true"
      >/</span>
    </template>
    <template v-else>
      <span
        class="dic-logo__word"
        aria-hidden="true"
      >Insiders.</span>
      <span
        class="dic-logo__slash"
        aria-hidden="true"
      >/</span>
      <span
        v-if="variant === 'horizontal'"
        class="dic-logo__geo"
        aria-hidden="true"
      >{{ geo }}</span>
    </template>
  </component>
</template>

<style scoped lang="scss">
.dic-logo {
  display: inline-flex;
  align-items: baseline;
  white-space: nowrap;
  line-height: 1;
  outline: none;
  text-decoration: none;
  gap: 0.22em;
  font-family: 'Syne', system-ui, sans-serif;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #141210;

  &__word {
    font-weight: 700;
    color: inherit;
    line-height: 1;
  }

  &__slash {
    font-family: 'Syne', system-ui, sans-serif;
    font-weight: 500;
    color: #d4a032;
    font-size: 0.88em;
    line-height: 1;
  }

  &__geo {
    font-family: 'DM Sans', system-ui, sans-serif;
    font-style: italic;
    font-weight: 400;
    color: #6e675e;
    font-size: 0.344em;
    letter-spacing: 0.005em;
    line-height: 1;
    align-self: baseline;
  }

  &--dark {
    color: #faf6ec;

    .dic-logo__geo {
      color: rgba(250, 246, 236, 0.65);
    }
  }

  &--mark {
    align-items: center;
    justify-content: center;
    gap: 0;
    background: #d4a032;
    color: #141210;
    border-radius: 22%;
    font-family: 'Syne', system-ui, sans-serif;
    font-weight: 800;
    letter-spacing: -0.04em;
    line-height: 1;

    .dic-logo__slash {
      font-family: inherit;
      font-weight: 800;
      font-size: 1em;
      color: inherit;
    }
  }

  &--mark.dic-logo--dark {
    background: #141210;
    color: #d4a032;

    .dic-logo__slash {
      color: #faf6ec;
    }
  }
}

a.dic-logo {
  color: #141210;

  &.dic-logo--dark {
    color: #faf6ec;
  }
}
</style>
